// pages/_app.tsx
import '@/styles/globals.css';
import { theme, themeCssVariables } from '@/lib/theme';
import { SITE_URL } from '@/lib/seoRoutes';
import { installZoomPingAbortGuard } from '@/lib/zoomPingAbortGuard';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import TagManager from 'react-gtm-module';

if (typeof window !== 'undefined') {
  installZoomPingAbortGuard();
}

const DEFAULT_TITLE =
  'DocMoonlight | Physician Coverage';
const DEFAULT_DESCRIPTION =
  'DocMoonlight connects imaging and infusion centers with onsite, virtual, and hybrid physician coverage while helping residents find flexible moonlighting shifts.';
const DEFAULT_IMAGE = `${SITE_URL}/images/logo.svg`;

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const cleanPath = router.asPath.split(/[?#]/)[0] || '/';
  const canonicalUrl = `${SITE_URL}${cleanPath === '/' ? '' : cleanPath}`;

  useEffect(() => {
    // extra safety in case something runs on the server
    if (typeof window === 'undefined') return;

    // avoid double init in React 18 dev StrictMode
    if ((window as any).__gtmInitialized) return;

    TagManager.initialize({ gtmId: 'GTM-N4C99WT' });
    (window as any).__gtmInitialized = true;
  }, []);

  return (
    <>
      <Head>
        <title>{DEFAULT_TITLE}</title>
        <meta
          key='viewport'
          name='viewport'
          content='width=device-width, initial-scale=1'
        />
        <meta
          key='description'
          name='description'
          content={DEFAULT_DESCRIPTION}
        />
        <meta
          key='keywords'
          name='keywords'
          content='DocMoonlight, radiology staffing, contrast coverage, infusion coverage, resident moonlighting, virtual physician supervision, onsite physician coverage, hybrid coverage'
        />
        <meta key='author' name='author' content='DocMoonlight' />
        <meta key='theme-color' name='theme-color' content={theme.colors.dark} />
        <meta key='robots' name='robots' content='index, follow' />
        <link key='canonical' rel='canonical' href={canonicalUrl} />
        <meta key='og:type' property='og:type' content='website' />
        <meta key='og:site_name' property='og:site_name' content='DocMoonlight' />
        <meta key='og:title' property='og:title' content={DEFAULT_TITLE} />
        <meta
          key='og:description'
          property='og:description'
          content={DEFAULT_DESCRIPTION}
        />
        <meta key='og:url' property='og:url' content={canonicalUrl} />
        <meta key='og:image' property='og:image' content={DEFAULT_IMAGE} />
        <meta
          key='twitter:card'
          name='twitter:card'
          content='summary_large_image'
        />
        <meta key='twitter:title' name='twitter:title' content={DEFAULT_TITLE} />
        <meta
          key='twitter:description'
          name='twitter:description'
          content={DEFAULT_DESCRIPTION}
        />
        <meta key='twitter:image' name='twitter:image' content={DEFAULT_IMAGE} />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'DocMoonlight',
              url: SITE_URL,
              logo: DEFAULT_IMAGE,
              description: DEFAULT_DESCRIPTION,
            }),
          }}
        />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: 'DocMoonlight',
              url: SITE_URL,
              description: DEFAULT_DESCRIPTION,
            }),
          }}
        />
        <style>{themeCssVariables}</style>
      </Head>
      <Component {...pageProps} />
    </>
  );
}
